import React from 'react'
import { Button, Field, Input } from '@/components/ds'
import { Ic } from '@/components/icons'
import { MascotFigure } from '@/components/Mascot'
import { useAuth } from '@/lib/auth'

/**
 * Single-admin sign-in. There is deliberately no sign-up link — the admin user
 * is created by hand in the Supabase dashboard (see src/lib/auth.tsx).
 *
 * The decoration is pure CSS art (blurred gradient blobs, a dot grid, small
 * bobbing motifs) so it costs no image requests and scales with the viewport.
 * All of it lives behind `aria-hidden` and freezes under prefers-reduced-motion;
 * styles are in src/index.css under "sign-in screen".
 */
export function LoginScreen({ themeToggle }: { themeToggle?: React.ReactNode }) {
  const { signIn } = useAuth()
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    const { error: err } = await signIn(email.trim(), password)
    if (err) {
      setError(err)
      setBusy(false)
    }
    // On success the AuthProvider swaps this screen out.
  }

  // 11px corners and a 46px minimum are the mock's, not the design system's —
  // the shared tokens stay 4px/36px for the dense screens behind the login.
  //
  // `flex: none` is load-bearing: Input carries `flex: 1` for its prefix shell
  // (a ROW flex container), but Field stacks its children in a COLUMN, where
  // that same rule resolves to `flex-basis: 0` on the vertical axis and beats
  // `height` outright — the field collapses to its 20px content box. Opting out
  // of flex sizing here lets the height below actually apply.
  //
  // No boxShadow: leaving it to the component preserves the focus ring, which
  // .login-card re-points at the accent via --border-focus.
  const fieldStyle: React.CSSProperties = {
    flex: 'none',
    borderRadius: 11,
    height: 46,
    background: 'var(--login-field-bg)',
  }

  return (
    <div className="login-page">
      <div className="login-bg" aria-hidden="true">
        <span className="login-blob b1" />
        <span className="login-blob b2" />
        <span className="login-blob b3" />
        <span className="login-blob b4" />
        <span className="login-dots" />
        <Motifs />
      </div>

      {themeToggle && <div className="login-theme">{themeToggle}</div>}

      <div className="login-shell">
        <div className="login-mascot" aria-hidden="true">
          <MascotFigure className="login-bunny" gradientId="login-fur" />
        </div>

        <div className="login-card">
          <div className="login-brand">
            <span className="login-mark">
              <Ic.send size={17} color="#fff" strokeWidth={2.2} />
            </span>
            <span className="login-wordmark">LeadMailer</span>
          </div>

          <p className="login-tagline">Warm, personal outreach — one inbox at a time.</p>

          <form onSubmit={submit} className="login-form">
            <Field label="Email" htmlFor="email">
              <Input
                id="email"
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={setEmail}
                placeholder="you@yourdomain.com"
                style={fieldStyle}
              />
            </Field>

            <Field label="Password" htmlFor="password" error={error ?? undefined}>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={setPassword}
                placeholder="••••••••"
                style={fieldStyle}
              />
            </Field>

            <Button
              type="submit"
              className="login-submit"
              fullWidth
              disabled={busy}
              icon={busy ? <Ic.spinner size={16} /> : <Ic.send size={16} strokeWidth={2.2} />}
              style={{ borderRadius: 11, height: 48, marginTop: 4 }}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}

/**
 * The scattered "graffiti" doodles. Stroked with currentColor so a single CSS
 * variable re-tints the whole set between light and dark.
 */
function Motifs() {
  const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const }
  return (
    <>
      <svg className="login-motif m1" viewBox="0 0 24 24" width="30" height="30">
        <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" {...s} />
        <path d="M3 7l9 6 9-6" {...s} />
      </svg>
      <svg className="login-motif m2" viewBox="0 0 24 24" width="20" height="20">
        <path d="M12 4v16M4 12h16" {...s} />
      </svg>
      <svg className="login-motif m3" viewBox="0 0 40 18" width="44" height="20">
        <path d="M2 12C6 2 10 2 14 10s8 8 12 0 8-8 12 0" {...s} />
      </svg>
      <svg className="login-motif m4" viewBox="0 0 24 24" width="28" height="28">
        <path d="M21.5 2.5L2.5 10l7.5 3 3 7.5 8.5-18z" {...s} strokeLinejoin="round" />
        <path d="M10 13l4.5-4.5" {...s} />
      </svg>
      <svg className="login-motif m5" viewBox="0 0 26 10" width="28" height="11">
        <circle cx="4" cy="5" r="2.4" fill="currentColor" />
        <circle cx="13" cy="5" r="2.4" fill="currentColor" />
        <circle cx="22" cy="5" r="2.4" fill="currentColor" />
      </svg>
      <svg className="login-motif m6" viewBox="0 0 24 24" width="22" height="22">
        <path d="M5 5l14 14M19 5L5 19" {...s} />
      </svg>
    </>
  )
}
